import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MANUAL_IT from "@/data/manualChapters.json";
import MANUAL_EN from "@/data/manualChapters.en.json";
import MANUAL_FR from "@/data/manualChapters.fr.json";
import { Book, Search, Download, ChevronRight, Printer, HelpCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { startAppTour } from "@/lib/tour";

export default function ManualePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language || "it").slice(0, 2);
  const MANUAL_CHAPTERS = lang === "en" ? MANUAL_EN : lang === "fr" ? MANUAL_FR : MANUAL_IT;
  const [activeSlug, setActiveSlug] = useState(MANUAL_CHAPTERS[0].slug);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  const filtered = useMemo(() => {
    if (!q.trim()) return MANUAL_CHAPTERS;
    const term = q.toLowerCase();
    return MANUAL_CHAPTERS.filter((c) =>
      c.title.toLowerCase().includes(term) ||
      c.intro.toLowerCase().includes(term) ||
      (c.steps || []).some((s) => s.toLowerCase().includes(term))
    );
  }, [q]);

  const active = MANUAL_CHAPTERS.find((c) => c.slug === activeSlug) || MANUAL_CHAPTERS[0];

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/manuale/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Manuale_utente.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("auto.errore_durante_la_generazione_del_pdf"));
    } finally {
      setDownloading(false);
    }
  };

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="manuale-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Documentazione</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.manual")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.guida_completa_passo_passo_con_schermate_di_ogni_sezione")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => startAppTour({ lang: i18n.language })}
            className="rounded-sm border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white"
            data-testid="btn-start-tour-manual"
          >
            <HelpCircle className="mr-2 h-4 w-4" /> {t("auto.tour_interattivo")}
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={downloading}
            className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]"
            data-testid="btn-download-manual-pdf"
          >
            <Download className="mr-2 h-4 w-4" /> {downloading ? "Generazione..." : "Scarica PDF"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Sidebar capitoli */}
        <div className="space-y-3 rounded-sm border border-slate-200 bg-white p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("auto.cerca_nel_manuale")}
              className="rounded-sm pl-8 text-sm"
              data-testid="manual-search"
            />
          </div>
          <nav className="space-y-0.5">
            {filtered.length === 0 && (
              <div className="p-4 text-center text-xs text-slate-400">{t("auto.nessun_capitolo")}</div>
            )}
            {filtered.map((c, idx) => {
              const isSel = c.slug === activeSlug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setActiveSlug(c.slug)}
                  data-testid={`manual-chapter-${c.slug}`}
                  className={`flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors ${
                    isSel ? "bg-[#0052FF] text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-sm text-[10px] font-mono font-bold ${
                    isSel ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                  }`}>{idx + 1}</span>
                  <span className="flex-1 truncate">{c.title}</span>
                  <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isSel ? "text-white" : "text-slate-400"}`} />
                </button>
              );
            })}
          </nav>
        </div>

        {/* Contenuto capitolo */}
        <article className="space-y-5 rounded-sm border border-slate-200 bg-white p-6 lg:p-8" data-testid="manual-content">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#0052FF]">
            <Book className="h-4 w-4" /> Capitolo {MANUAL_CHAPTERS.findIndex((c) => c.slug === active.slug) + 1}
          </div>
          <h2 className="text-2xl font-bold text-slate-900 lg:text-3xl" data-testid="manual-title">{active.title}</h2>
          <p className="text-base text-slate-700 leading-relaxed">{active.intro}</p>

          {active.image && (
            <figure className="overflow-hidden rounded-sm border border-slate-200 shadow-sm">
              <img
                src={active.image}
                alt={active.title}
                className="w-full"
                loading="lazy"
                data-testid={`manual-image-${active.slug}`}
              />
              <figcaption className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                Schermata di riferimento — {active.title}
              </figcaption>
            </figure>
          )}

          {active.steps && active.steps.length > 0 && (
            <div>
              <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Passi operativi</h3>
              <ol className="space-y-3">
                {active.steps.map((s, i) => (
                  <li key={i} className="flex gap-3" data-testid={`manual-step-${active.slug}-${i}`}>
                    <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-[#0052FF] font-mono text-xs font-bold text-white">{i + 1}</span>
                    <span className="text-sm text-slate-700 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Nav prev/next */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
            {(() => {
              const idx = MANUAL_CHAPTERS.findIndex((c) => c.slug === active.slug);
              const prev = idx > 0 ? MANUAL_CHAPTERS[idx - 1] : null;
              const next = idx < MANUAL_CHAPTERS.length - 1 ? MANUAL_CHAPTERS[idx + 1] : null;
              return (
                <>
                  {prev ? (
                    <Button variant="outline" onClick={() => setActiveSlug(prev.slug)} className="rounded-sm" data-testid="manual-prev">
                      ← {prev.title}
                    </Button>
                  ) : <span />}
                  {next ? (
                    <Button onClick={() => setActiveSlug(next.slug)} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="manual-next">
                      {next.title} →
                    </Button>
                  ) : <span />}
                </>
              );
            })()}
          </div>
        </article>
      </div>
    </div>
  );
}
